import { AxiosResponse } from "axios";
import { oc } from "ts-optchain";

import { DealernetApi } from "./DealernetApi";

import { EventTrackingService, ShoppingCartService } from ".";

import { IPagedList, IPartListBuilderTransferItemsDto, ISelectListOption, Nullable } from "@/types";
import { IGetOemsRequest as IGetOemsInput } from "./ShoppingCartService";


export class QuickOrderService {
  public static async getOemsList(input: IGetOemsInput): Promise<IPagedList<ISelectListOption>> {
    const data = await ShoppingCartService.GetOems(input);

    return {
      ...data,
      Items: data.Items.map((x) => {
        const code = x.Id === 0 ? "" : x.Id;

        return {
          label: code === "" ? "Optional" : x.OemDescription,
          code,
        };
      }),
    };
  }

  public static async addToCart(input: IAddQuickOrderItemsToCartInput): Promise<any> {

    const accountContext = {
      userId: input.userId,
      accountId: input.accountId,
    };

    const cart = await ShoppingCartService.GetShoppingCartSummary(accountContext);

    const response = await DealernetApi.client().request({
      method: "post",
      url: "PartList/TransferItems",
      params: {
        userId: input.userId,
        accountId: input.accountId,
        listName: ""
      },
      data: {
        SourceListId: input.sourceListId,
        SourceListItemIds: input.sourceItemIds,
        TargetListId: cart.Id,
        RemoveImportedItemsFromSource: input.removeImportedItemsFromSource,
        IgnoreInvalidItems: true
      },
    });

    const result = oc(response as AxiosResponse<IPartListBuilderTransferItemsDto>).data();

    if (result && result.Success) {
      return {
        partListId: result.TargetListId
      }
    }

    return Promise.reject("Add to list failed");
  }

  public static async addItemsToList(input: IAddQuickOrderItemsToListInput): Promise<IAddQuickOrderItemsToListResult> {

    const response = await DealernetApi.client().request({
      method: "post",
      url: "PartList/TransferItems",
      params: {
        userId: input.userId,
        accountId: input.accountId,
        listName: input.newListName
      },
      data: {
        SourceListId: input.sourceListId,
        SourceListItemIds: input.sourceItemIds,
        TargetListId: input.newListId,
        RemoveImportedItemsFromSource: input.removeImportedItemsFromSource,
        IgnoreInvalidItems: false
      },
    });

    const result = oc(response as AxiosResponse<IPartListBuilderTransferItemsDto>).data();

    if (result && result.Success) {
      return {
        partListId: result.TargetListId
      }
    }

    return Promise.reject("Add to list failed");
  }

  public static async uploadFile(input: IPartListUpload): Promise<IPartListUploadResult> {

    const data = new FormData();

    data.append("fileType", input.fileType);

    if (input.partListId) {
      data.append("listId", String(input.partListId));
    }

    data.append("userAudiences", input.userAudiences.join(","));

    input.files.forEach((file, i) => {
      data.append(`file[${i}]`, file);
    });

    const response = await  DealernetApi.client().request({
      method: "post",
      url: "PartList/UploadPartList",
      params: {
        userId: input.userId,
        accountId: input.accountId
      },
      data,
      onUploadProgress: input.onUploadProgress
    });

    const result = oc(response as AxiosResponse<any>).data({});

    if (result.Error) {
      return {
        errorMessage: result.Error,
        success: false
      }
    }

    return {
      success: true
    };
  }

  public static getShowDealerBinPref(): boolean {
    return localStorage.getItem("quick-order-show-location") === "yes";
  }

  public static setShowDealerBinPref(show: boolean) {
    localStorage.setItem("quick-order-show-location", show ? "yes" : "no");

    EventTrackingService.trackEvent({
      category: "QuickOrder",
      action: "DealerBinLocationToggle",
      label: show ? "Showing DealerBinLocation" : "Hiding DealerBinLocation"
    });
  }

  public static getShowDealerSkuPref(): boolean {
    return localStorage.getItem("quick-order-show-sku") === "yes";
  }

  public static setShowDealerSkuPref(show: boolean) {
    localStorage.setItem("quick-order-show-sku", show ? "yes" : "no");

    EventTrackingService.trackEvent({
      category: "QuickOrder",
      action: "DealerPartNumberToggle",
      label: show ? "Showing DealerPartNumber" : "Hiding DealerPartNumber"
    });
  }

  public static getShowOemPricePref(): boolean {
    return localStorage.getItem("quick-order-show-oem-price") === "yes";
  }

  public static setShowOemPricePref(show: boolean) {
    localStorage.setItem("quick-order-show-oem-price", show ? "yes" : "no");

    EventTrackingService.trackEvent({
      category: "QuickOrder",
      action: "OemListPriceToggle",
      label: show ? "Showing OemListPrice" : "Hiding OemListPrice"
    });
  }
}

export interface IAddQuickOrderItemsToCartInput {
  accountId: number;
  userId: number;
  sourceListId: number;
  sourceItemIds: number[];
  removeImportedItemsFromSource: boolean;
}

export interface IAddQuickOrderItemsToListInput {
  accountId: number;
  userId: number;
  sourceListId: number;
  sourceItemIds: number[];
  removeImportedItemsFromSource: boolean;
  newListId: number;
  newListName?: Nullable<string>;
}

export interface IAddQuickOrderItemsToListResult {
  partListId: number;
}

export interface IPartListUploadResult {
  errorMessage?: string;
  success: boolean;
}

export interface IPartListUpload {
  accountId: number;
  userId: number;
  userAudiences: number[],
  fileType: any;
  partListId: any
  files: File[],
  onUploadProgress?: (progressEvent: any) => void
}
